import dayjs from 'dayjs'
import type { Dayjs /* , ManipulateType*/ } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import customParseFormat from 'dayjs/plugin/customParseFormat'

import 'dayjs/locale/es'
import 'dayjs/locale/en'
import 'dayjs/locale/gl'

import {
  AvailableLanguage,
  getInitialLocale
} from 'src/helpers/language/AvailableLanguages'
import { getLocale } from 'src/helpers/language/TranslationHelper'
import { HistoryModelDate } from '@models/api/HistoryModelDate'
import { Hours } from 'src/typings/time'
import { toRaw } from 'vue'

dayjs.locale(getInitialLocale())

// Registers dayJs plugins
dayjs.extend(timezone)
dayjs.extend(utc)
dayjs.extend(localizedFormat)
dayjs.extend(customParseFormat)

export const formatDB = 'YYYY-MM-DDTHH:mm:ss'
export const simulationFormat = 'YYYY_MM_DD_HH_mm'
export const formatReadable = 'DD/MM/YYYY HH:mm'
const formatServer = 'YYYY-MM-DD'
export const ISO_8601 = 'YYYY-MM-DDTHH:mm:ss.SSSZ'
export const TDateISODateFormat = 'YYYY-MM-DD'

export default {
  parseDate(date: string | Dayjs | Date, format?: string): Dayjs | null {
    if (!date) {
      return null
    }

    if (format) {
      return dayjs(date, format)
    } else if (typeof date === 'string') {
      // Check for 'DD/MM/YYYY HH:mm' format
      const pattern = new RegExp(/\d{1,2}[/]\d{1,2}[/]\d{4} (\d\d):[0-5]\d$/)
      const hasFormat = pattern.test(date)
      if (hasFormat) {
        return dayjs(date, formatReadable)
      } else {
        return dayjs(date)
      }
    } else {
      return dayjs(date)
    }
  },

  formatDate(date: string | Dayjs | Date | null, format = 'YYYY-MM-DD') {
    if (!date) {
      return ''
    }

    if (date === '') {
      return date
    } else {
      if (typeof date === 'string' || date instanceof Date) {
        date = this.parseDate(date)
      }
      return date?.format(format)
    }
  },

  getShortDayOfWeek(date: Dayjs) {
    const weekDay = new Date(+date).toLocaleString(getLocale(), {
      weekday: 'short'
    })
    const numberDay = new Date(+date).toLocaleString(getLocale(), {
      day: 'numeric'
    })
    return `${weekDay} ${numberDay}`
  },

  utc(date?: string | Dayjs | Date, format?: string) {
    return dayjs.utc(date, format)
  },

  getCurrentDateDB() {
    // return dayjs().format(formatDB)
    return dayjs().toISOString()
  },

  currentDate() {
    return this.now().format(formatReadable)
  },

  currentHourMinute() {
    return this.now().format('HH:mm')
  },

  currentDateServer() {
    return dayjs().format(formatServer)
  },

  /* add(date: any, value: number, field: ManipulateType) {
    return this.parseDate(date)?.add(value, field)
  },

  subtract(date: any, value: number, field: ManipulateType) {
    return this.parseDate(date)?.subtract(value, field)
  },*/

  convertServiceDateToFormatDB(date: any) {
    return dayjs(date, formatServer).format(formatDB)
  },

  convertServiceDateToFormatServer(date: any) {
    return dayjs(date, formatServer)
  },

  now() {
    return dayjs()
  },

  utcOffset(unit: 'hours' | 'minutes' = 'hours') {
    const minuteOffset = dayjs().utcOffset()
    let offset = minuteOffset

    if (unit === 'hours') {
      offset = offset / 60
    }

    return offset
  },

  hoursUntilNextDay(date?: Dayjs) {
    if (!date) {
      date = dayjs()
    }

    return 24 - date.hour()
  },

  dateWithMillis(millis: number) {
    return dayjs(millis)
  },

  isValid(date: any) {
    return this.parseDate(date)?.isValid()
  },

  convertJSDateToDayJS(date: any) {
    return dayjs(date)
  },

  addHoursToDate(date: any, hours: number) {
    return dayjs(date).add(hours, 'hour')
  },

  setLocale(locale: AvailableLanguage) {
    dayjs.locale(locale)
  },

  getHourInLocalTime(utcHour: number): number {
    const uctDate = dayjs().utc().hour(utcHour)
    return uctDate.local().hour()
  },

  getAllDisabledDatesBetwen(enabledDates: Dayjs[]): Dayjs[] {
    const allDisabledDates: Dayjs[] = []
    const firstEnabledDate = enabledDates[0]
    const lastEnabledDate = enabledDates[enabledDates.length - 1]
    const firstEnabledDateDay = dayjs(firstEnabledDate)
    const lastEnabledDateDay = dayjs(lastEnabledDate)
    const daysBetween = lastEnabledDateDay.diff(firstEnabledDateDay, 'day')
    for (let i = 1; i < daysBetween; i++) {
      const disabledDate = firstEnabledDateDay.add(i, 'day')
      if (!this.dateArrayContainsDate(enabledDates, disabledDate)) {
        allDisabledDates.push(disabledDate)
      }
    }
    return allDisabledDates
  },

  dateArrayContainsDate(dateArray: Dayjs[], date: Dayjs): boolean {
    return dateArray.some((dateFromArray: Dayjs) => {
      return dateFromArray.isSame(date, 'day')
    })
  },

  convertHistoryModelDatesToDayJS(modelDates: HistoryModelDate[]): Dayjs[] {
    const dates: Dayjs[] = []
    modelDates.forEach((modelDate) => {
      dates.push(...this.convertHistoryModelDateToDayJS(modelDate))
    })
    return dates
  },

  convertHistoryModelDateToDayJS(modelDate: HistoryModelDate): Dayjs[] {
    const dates: Dayjs[] = []
    if (modelDate.outputs) {
      modelDate.outputs.forEach((output) => {
        const date = dayjs.utc(modelDate.date).hour(+output)
        dates.push(date)
      })
    } else {
      const date = dayjs.utc(modelDate.date)
      dates.push(date)
    }
    return dates
  },

  getDatesOfDay(dates: Dayjs[], day: Dayjs): Dayjs[] {
    return dates.filter((date) => date.isSame(day, 'day'))
  },

  isDateSameOrBefore(date: Dayjs, dateToCompare: Dayjs) {
    return date.isSame(dateToCompare) || date.isBefore(dateToCompare)
  },

  isDateSameOrAfter(date: Dayjs, dateToCompare: Dayjs) {
    return date.isSame(dateToCompare) || date.isAfter(dateToCompare)
  },

  sortDates(dates: Dayjs[]) {
    return dates.sort((a, b) => {
      if (a.isBefore(b)) {
        return -1
      } else if (a.isAfter(b)) {
        return 1
      } else {
        return 0
      }
    })
  },

  findMoreAproxDates(
    dates: Dayjs[],
    objectiveDate: Dayjs
  ): { moreRecentBefore: Dayjs; moreRecentAfter: Dayjs; moreAprox: Dayjs } {
    const dateExact = dates.find((date) => date.isSame(objectiveDate, 'minute'))
    if (dateExact) {
      return {
        moreRecentBefore: dateExact,
        moreRecentAfter: dateExact,
        moreAprox: dateExact
      }
    }
    const datesShorted = this.sortDates(dates)
    const datesBefore = datesShorted.filter((date) =>
      date.isBefore(objectiveDate)
    )
    const datesAfter = datesShorted.filter((date) =>
      date.isAfter(objectiveDate)
    )
    const moreRecentBefore = datesBefore[datesBefore.length - 1]
    const moreRecentAfter = datesAfter[0]
    let moreAprox = objectiveDate
    if (moreRecentAfter && moreRecentBefore) {
      const diffBefore = objectiveDate.diff(moreRecentBefore)
      const diffAfter = moreRecentAfter.diff(objectiveDate)
      if (diffBefore < diffAfter) {
        moreAprox = moreRecentBefore
      } else {
        moreAprox = moreRecentAfter
      }
    } else if (moreRecentBefore) {
      moreAprox = moreRecentBefore
    } else if (moreRecentAfter) {
      moreAprox = moreRecentAfter
    }
    return {
      moreRecentBefore: moreRecentBefore,
      moreRecentAfter: moreRecentAfter,
      moreAprox
    }
  },

  findMoreRecentBeforeAproxDate(dates: Dayjs[], objectiveDate: Dayjs): Dayjs {
    const { moreRecentBefore } = this.findMoreAproxDates(dates, objectiveDate)
    return moreRecentBefore || objectiveDate
  },

  findMoreAproxDate(dates: Dayjs[], objectiveDate: Dayjs): Dayjs {
    const { moreRecentBefore, moreRecentAfter, moreAprox } =
      this.findMoreAproxDates(dates, objectiveDate)
    if (moreRecentBefore && moreRecentAfter) {
      return moreAprox
    } else if (moreRecentBefore) {
      return moreRecentBefore
    } else if (moreRecentAfter) {
      return moreRecentAfter
    } else {
      return objectiveDate
    }
  },

  findClosestDateNow(dates: Dayjs[]) {
    return this.findMoreAproxDate(dates, this.now())
  },

  findIndexMoreAproxDate(dates: Dayjs[], objectiveDate: Dayjs): number {
    const date = this.findMoreAproxDate(dates, objectiveDate)
    return dates.findIndex((dateFromArray) => dateFromArray.isSame(date))
  },

  getDatesBetweenDates(
    dates: Dayjs[],
    initialDate: Dayjs,
    finalDate: Dayjs
  ): Dayjs[] {
    const sortedDates = this.sortDates(dates)
    const comparate = 'hour'
    return sortedDates.filter((date) => {
      return (
        (date.isAfter(initialDate, comparate) &&
          date.isBefore(finalDate, comparate)) ||
        date.isSame(initialDate, comparate) ||
        date.isSame(finalDate, comparate)
      )
    })
  },

  getPastDates(initialDate: Dayjs, hoursToEnd: Hours, stepHours: Hours) {
    const hoursInMonth = 600
    if (stepHours >= hoursInMonth) {
      return this.getPastDatesMonthly(initialDate, hoursToEnd)
    }
    const endDate = initialDate.subtract(hoursToEnd, 'hours')
    const dates = []
    for (
      let activeDate = initialDate;
      activeDate.isAfter(endDate) || activeDate.isSame(endDate);
      activeDate = activeDate.subtract(stepHours, 'hours')
    ) {
      dates.push(toRaw(activeDate))
    }
    return dates
  },

  getPastDatesMonthly(initialDate: Dayjs, hoursToEnd: Hours) {
    const endDate = initialDate.subtract(hoursToEnd, 'hours')
    const dates = []
    for (
      let activeDate = initialDate;
      activeDate.isAfter(endDate) || activeDate.isSame(endDate);
      activeDate = activeDate.subtract(1, 'month')
    ) {
      dates.push(activeDate)
    }
    return dates
  },

  getFutureDatesMonthly(initialDate: Dayjs, hoursToEnd: Hours) {
    const endDate = initialDate.add(hoursToEnd, 'hours')
    const dates = []
    for (
      let activeDate = initialDate;
      activeDate.isBefore(endDate) || activeDate.isSame(endDate);
      activeDate = activeDate.add(1, 'month')
    ) {
      dates.push(activeDate)
    }
    return dates
  },

  getFutureDates(initialDate: Dayjs, hoursToEnd: Hours, stepHours: Hours) {
    const hoursInMonth = 600
    if (stepHours >= hoursInMonth) {
      return this.getFutureDatesMonthly(initialDate, hoursToEnd)
    }
    const endDate = initialDate.add(hoursToEnd, 'hours')
    const dates = []
    for (
      let activeDate = initialDate;
      activeDate.isBefore(endDate) || activeDate.isSame(endDate);
      activeDate = activeDate.add(stepHours, 'hours')
    ) {
      dates.push(activeDate)
    }
    return dates
  },

  getAvailableDatesMinDiff(dates: Dayjs[]): number {
    if (dates.length < 2) {
      return 0
    }
    const date1 = dates[0]
    const date2 = dates[1]
    return date2.diff(date1, 'hours')
  },

  getAvailableDatesMaxDiff(dates: Dayjs[]): number {
    if (dates.length < 2) {
      return 0
    }
    const date1 = dates[0]
    const maxDate = dates[dates.length - 1]
    const maxDiff = date1.diff(maxDate, 'hours')
    return maxDiff
  },

  getIntervalOptionsOfDates(dates: Dayjs[]): number[] {
    if (dates.length < 2) {
      return []
    }
    const date2 = dates[1]
    const maxDate = dates[dates.length - 1]
    const minDiff = date2.diff(dates[0], 'hours')
    const maxDiff = maxDate.diff(dates[0], 'hours')
    // [mindiff..., hoursDiference, maxDiff...]
    const options: number[] = []
    for (let i = 1; i <= maxDiff; i++) {
      const iMultiple = minDiff * i
      if (iMultiple <= maxDiff && iMultiple <= 24) {
        options.push(iMultiple)
      }
    }
    if (options.length > 19) {
      const optionsFiltered = options.filter((option) => {
        return (
          option % 24 === 0 ||
          option == 1 ||
          option % 3 === 0 ||
          option == options[options.length - 1]
        )
      })
      return optionsFiltered
    }
    return options
  }
}
